import React, { useEffect, useRef, useMemo } from 'react'
import Footer from '../Footer'

const ResultPage = ({ selections, onRestart, quizResult, config, traitTestSubmit }) => {
  const categories = config.categories
  const isTraitsTest = config.apiEndpoint === 'traitstest'
  
  const topRef = useRef(null)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const resultData = isTraitsTest ? traitTestSubmit : quizResult

  const getScoreValue = (key) => {
    if (!resultData) return 0

    if (isTraitsTest) {
      const scoreKey = key.charAt(0).toLowerCase() + key.slice(1) + 'Score'
      return resultData[scoreKey] ?? 0
    } else {
      return resultData[key] ?? 0
    }
  }

  const getLevelValue = (key) => {
    if (!resultData || !isTraitsTest) return ''
    const levelKey = key.charAt(0).toLowerCase() + key.slice(1) + 'Level'
    return resultData[levelKey] ?? ''
  }

  // ✅ Sort categories by score in descending order (highest first)
  const sortedCategories = useMemo(() => {
    const categoriesArray = Object.entries(categories).map(([key, label]) => ({
      key,
      label,
      score: getScoreValue(key),
      level: getLevelValue(key)
    }))

    return categoriesArray.sort((a, b) => b.score - a.score)
  }, [categories, resultData])

  return (
    <div className="flex-1 p-6 bg-white" ref={topRef}>
      <div className="max-w-full mx-auto">
        <div className="bg-white border border-[#F1F2F9] shadow-[0_1px_8px_rgba(25,33,61,0.06)] rounded-[12px] overflow-hidden">
          <div className="p-4 md:p-8 bg-white">
            <div className="text-center mb-6">
              <h1 className="font-lato not-italic font-bold text-[28px] md:text-[48px] leading-[150%] text-[#FF8E1C] mb-2">
                Congratulations!
              </h1>
            </div>

            <div className="text-center mb-6">
              {isTraitsTest ? (
                <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black mt-2">
                  The Score For Your {Object.keys(categories).length} Character Traits Assessment Are:
                </p>
              ) : (
                <>
                  <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black mt-2">
                    You have successfully identified your inner needs.
                  </p>
                  <p className="font-lato not-italic font-medium text-[16px] leading-[150%] text-black mt-2">
                    Your {Object.keys(categories).length} inner needs are:
                  </p>
                </>
              )}
            </div>

            {/* Table Section - Sorted by Score (Highest First) */}
            <div className="mb-6 max-w-[600px] mx-auto">
              <div className={
                isTraitsTest
                  ? "overflow-auto w-[300px] md:w-[100%] lg:w-[100%] border border-gray-200 rounded"
                  : "overflow-hidden border border-gray-200 rounded"
              }>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#FFF6EB] border-b border-gray-200">
                      <th className="px-8 py-3 text-left font-lato not-italic font-medium text-[14px] leading-[150%] text-[#475467]">
                        {isTraitsTest ? "Traits" : "Inner Need"}
                      </th>
                      <th className="px-8 py-3 text-left font-lato not-italic font-medium text-[14px] leading-[150%] text-[#475467]">
                        Your Score
                      </th>
                      {isTraitsTest && (
                        <th className="px-8 py-3 text-left font-lato not-italic font-medium text-[14px] leading-[150%] text-[#475467]">
                          Score Level
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {/* ✅ Using sortedCategories instead of Object.entries(categories) */}
                    {sortedCategories.map((category, index) => (
                      <tr key={category.key} className="bg-white border-b last:border-0 border-gray-200 text-start">
                        <td className="px-8 py-3 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#1D2939]">
                          {category.label}
                        </td>
                        <td className="px-8 py-3 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#1D2939]">
                          {category.score}
                        </td>
                        {isTraitsTest && (
                          <td className="px-8 py-3 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#1D2939] capitalize">
                            {category.level.toLowerCase()}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <p className="mb-2 font-lato not-italic font-medium text-[16px] leading-[150%] text-black">
                {isTraitsTest ? (
                  "Contact Your Coach For Further Guidance"
                ) : (
                  <>
                    The inner need with the highest score is your topmost inner need.<br />
                    The inner need with the second-highest score is your second topmost inner need.<br />
                    To understand your inner needs in detail, contact your coach.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={onRestart}
            className="px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] transition-all duration-200 bg-[#FF8E1C] text-white hover:bg-[#E67E0C]"
          >
            Go Home
          </button>
        </div>
        <div className="mt-6">
          <Footer />
        </div>
      </div>
    </div>
  )
}

export default ResultPage